import app from 'flarum/common/app';

/**
 * Club summaries shipped with the forum payload (see AddForumClubs.php).
 *
 * @returns {Array<{id: number, discussionId: number, slug: string, name: string, icon: ?string, color: ?string, coverUrl: ?string, leaders: Array}>}
 */
export function allClubs() {
  return app.forum.attribute('happydigitalClubs') || [];
}

export function clubById(id) {
  const clubId = Number(id);

  return allClubs().find((club) => club.id === clubId) || null;
}

/**
 * @param {Discussion} discussion
 * @returns {object|null}
 */
export function clubForDiscussion(discussion) {
  if (!discussion) return null;

  const discussionId = Number(discussion.id());

  return allClubs().find((club) => club.discussionId === discussionId) || null;
}

/**
 * Replace (or add) a club in the forum payload after an edit, so badges and
 * headers update without a page reload. Pass `null` as summary to remove.
 *
 * @param {number} clubId
 * @param {object|null} summary
 */
export function updateClubSummary(clubId, summary) {
  const others = allClubs().filter((club) => club.id !== Number(clubId));

  app.forum.pushAttributes({ happydigitalClubs: summary ? [...others, summary] : others });
}

/**
 * The user's clubs with their role in each, from AddUserClubs.php.
 *
 * @param {User} user
 * @returns {Array<{club: object, role: ?string}>}
 */
export function clubsOfUser(user) {
  const memberships = (user && user.attribute('happydigitalClubs')) || [];

  return memberships
    .map((membership) => ({ club: clubById(membership.clubId), role: membership.role }))
    .filter((membership) => membership.club);
}

/**
 * The cover as a CSS custom property, the same way core passes a dynamic
 * colour to Badge (`--badge-bg`). Layout and sizing stay in forum.less.
 *
 * @param {{coverUrl: ?string}} club
 * @returns {object|undefined}
 */
export function coverStyle(club) {
  if (!club || !club.coverUrl) return undefined;

  return { '--club-cover': `url("${String(club.coverUrl).replace(/"/g, '%22')}")` };
}

export function apiUrl(path) {
  return app.forum.attribute('apiUrl') + '/happydigital-clubs' + (path || '');
}

/**
 * Keep only the summary fields from a list item returned by the API.
 */
export function toSummary(item) {
  const { id, discussionId, slug, name, icon, color, coverUrl, pinnedPostId, leaders } = item;

  return { id, discussionId, slug, name, icon, color, coverUrl, pinnedPostId, leaders };
}

/**
 * Whether the current user runs this club: its president or a vice, or
 * someone allowed to manage clubs.
 *
 * @param {object} club
 * @returns {boolean}
 */
export function canLeadClub(club) {
  if (!club || !app.session.user) return false;

  if (app.forum.attribute('happydigitalClubsCanManage')) return true;

  return (club.leaders || []).some((leader) => String(leader.userId) === app.session.user.id());
}
